import React, { useState } from 'react';
import { Box, Button, Heading, Stack, Text } from '@indeed/ifl-components';

// ── Config ──────────────────────────────────────────────────────────────────

export interface ProctorOption {
    /** Proctor test name (matches src/server/proctor/specifications). */
    name: string;
    /** Lowest selectable bucket value (typically -1 = inactive). */
    min: number;
    /** Highest selectable bucket value. */
    max: number;
    /** Default bucket value when the page loads. */
    defaultValue: number;
    /** Human-readable bucket legend shown under the field. */
    buckets: string;
}

// Proctor buckets that meaningfully change the Auto Apply / setup experience.
// Values >= 0 are appended to generated links as `prforceGroups`; -1 (inactive)
// is omitted, so a link only forces the buckets you explicitly opt into.
export const SUPPORTED_PROCTORS: ProctorOption[] = [
    {
        name: 'auto_apply_main_tog',
        min: -1,
        max: 1,
        defaultValue: 1,
        buckets: '-1 inactive · 0 control · 1 rba',
    },
    {
        name: 'rnhp_auto_apply_banner_tst',
        min: -1,
        max: 2,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test · 2 progressive_automation',
    },
    {
        name: 'auto_apply_rebranding_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test (Apply For Me)',
    },
    {
        name: 'apply_for_me_sfm_tog',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 disabled · 1 enabled',
    },
    {
        name: 'ia_auto_apply_preference_m1_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test',
    },
    {
        name: 'js_pro_auto_apply_sq_prefill_tst',
        min: -1,
        max: 5,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test · 2-5 reserved',
    },
    {
        name: 'ia_dq_questions_prefill_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test (show DQ autofill toggle)',
    },
    {
        name: 'auto_apply_setup_review_contact_info',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test',
    },
    {
        name: 'ia_phone_number_verification_tst',
        min: -1,
        max: 1,
        defaultValue: 0,
        buckets: '-1 inactive · 0 control (verification off) · 1 test',
    },
    {
        name: 'ia_rbs_relax_sq_requirement_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test (relax SQ requirement)',
    },
];

/** A proctor added ad-hoc via the panel (not part of SUPPORTED_PROCTORS). */
export interface CustomProctor {
    name: string;
    value: number;
}

/** Selectable bucket values offered for custom proctors. -1 leaves it unforced. */
export const CUSTOM_PROCTOR_VALUE_OPTIONS = [-1, 0, 1, 2, 3, 4, 5];

/** Build a `prforceGroups` value (e.g. `auto_apply_main_tog1,rnhp_auto_apply_banner_tst2`)
 * from the selected buckets, including only forced (>= 0) values. */
export const buildProctorParam = (
    values: Record<string, number>,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): string =>
    options
        .map((proctor) => ({ name: proctor.name, value: values[proctor.name] }))
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');

/** Join supported + custom forced buckets into a single `prforceGroups` string. */
export const buildPrforceGroups = (
    values: Record<string, number>,
    customProctors: CustomProctor[] = [],
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): string => {
    const customParam = customProctors
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');
    return [buildProctorParam(values, options), customParam].filter(Boolean).join(',');
};

const parseBucketValue = (raw: string): number => {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
};

/** Split a `prforceGroups` token (`auto_apply_main_tog1`) into name + bucket. */
export const parseProctorToken = (
    token: string,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): { name: string; value: number } | null => {
    const trimmed = token.trim();
    if (!trimmed) return null;

    const matchingOption = [...options]
        .sort((left, right) => right.name.length - left.name.length)
        .find(
            (option) =>
                trimmed.startsWith(option.name) && /^\d+$/.test(trimmed.slice(option.name.length)),
        );

    if (matchingOption) {
        return {
            name: matchingOption.name,
            value: parseInt(trimmed.slice(matchingOption.name.length), 10),
        };
    }

    const match = trimmed.match(/^(.*)(\d+)$/);
    if (!match || match[1] === '') return null;
    return { name: match[1], value: parseInt(match[2], 10) };
};

export interface ProctorOverridesInitialState {
    proctorValues?: Record<string, number>;
    customProctors?: CustomProctor[];
}

/**
 * Turn a `prforceGroups` search param into panel state.
 * `null` means the param is absent → caller should keep hook defaults.
 * A string (including `''`) means the param is present: tokens are forced,
 * every other supported proctor is unforced (`-1`), and unknown names are custom.
 */
export const proctorStateFromPrforceGroups = (
    prforceGroups: string | null,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): ProctorOverridesInitialState | undefined => {
    if (prforceGroups === null) return undefined;

    const proctorValues = Object.fromEntries(options.map((option) => [option.name, -1]));
    const customProctors: CustomProctor[] = [];
    const optionsByName = new Map(options.map((option) => [option.name, option]));

    prforceGroups.split(',').forEach((token) => {
        const parsed = parseProctorToken(token, options);
        if (!parsed) return;
        const option = optionsByName.get(parsed.name);
        if (option) {
            if (parsed.value < option.min || parsed.value > option.max) return;
            proctorValues[parsed.name] = parsed.value;
            return;
        }
        const existingIndex = customProctors.findIndex((proctor) => proctor.name === parsed.name);
        if (existingIndex >= 0) {
            customProctors[existingIndex] = parsed;
        } else {
            customProctors.push(parsed);
        }
    });

    return { proctorValues, customProctors };
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface ProctorOverridesController {
    options: ProctorOption[];
    proctorValues: Record<string, number>;
    setProctorValue: (name: string, raw: string) => void;
    /** Ad-hoc proctors added via the panel, on top of `options`. */
    customProctors: CustomProctor[];
    /** Add (or, if the name already exists, update) a custom proctor. */
    setCustomProctor: (name: string, value: number) => void;
    removeCustomProctor: (name: string) => void;
    /** Comma-joined forced buckets, ready to drop into a `prforceGroups` param. */
    prforceGroups: string;
}

/** Owns the editable bucket state for a proctor-override panel and derives the
 * `prforceGroups` string. Shared by the test pages so both stay in sync. */
export const useProctorOverrides = (
    options: ProctorOption[] = SUPPORTED_PROCTORS,
    initial?: ProctorOverridesInitialState,
): ProctorOverridesController => {
    const [proctorValues, setProctorValues] = useState<Record<string, number>>(() => ({
        ...Object.fromEntries(options.map((proctor) => [proctor.name, proctor.defaultValue])),
        ...initial?.proctorValues,
    }));
    const [customProctors, setCustomProctors] = useState<CustomProctor[]>(
        () => initial?.customProctors ?? [],
    );

    const setProctorValue = (name: string, raw: string) => {
        setProctorValues((prev) => ({ ...prev, [name]: parseBucketValue(raw) }));
    };

    const setCustomProctor = (name: string, value: number) => {
        const trimmed = name.trim();
        // Ignore blanks and names that would shadow a predefined proctor.
        if (!trimmed || options.some((proctor) => proctor.name === trimmed)) return;
        setCustomProctors((prev) => [
            ...prev.filter((proctor) => proctor.name !== trimmed),
            { name: trimmed, value },
        ]);
    };

    const removeCustomProctor = (name: string) => {
        setCustomProctors((prev) => prev.filter((proctor) => proctor.name !== name));
    };

    const prforceGroups = buildPrforceGroups(proctorValues, customProctors, options);

    return {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    };
};

// ── Panel ──────────────────────────────────────────────────────────────────────

interface ProctorOverridesPanelProps {
    controller: ProctorOverridesController;
    /** Short blurb explaining where the forced buckets end up. */
    description?: React.ReactNode;
    /** Fired with the next `prforceGroups` after a field is committed (blur / add / remove). */
    onCommit?: (prforceGroups: string) => void;
}

/** Sticky side panel of number fields, one per supported proctor, plus a live
 * `prforceGroups` preview. Presentational — state lives in `useProctorOverrides`. */
export const ProctorOverridesPanel: React.FC<ProctorOverridesPanelProps> = ({
    controller,
    description,
    onCommit,
}) => {
    const {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    } = controller;

    // Local state for the "add proctor" form.
    const [newName, setNewName] = useState('');
    const [newValue, setNewValue] = useState(1);

    const commitWith = (
        nextValues: Record<string, number> = proctorValues,
        nextCustom: CustomProctor[] = customProctors,
    ) => {
        onCommit?.(buildPrforceGroups(nextValues, nextCustom, options));
    };

    const customProctorsWithValue = (name: string, value: number): CustomProctor[] =>
        customProctors.map((entry) => (entry.name === name ? { ...entry, value } : entry));

    const handleAdd = () => {
        const trimmed = newName.trim();
        setCustomProctor(newName, newValue);
        setNewName('');
        setNewValue(1);
        if (!trimmed || options.some((proctor) => proctor.name === trimmed)) return;
        commitWith(proctorValues, [
            ...customProctors.filter((proctor) => proctor.name !== trimmed),
            { name: trimmed, value: newValue },
        ]);
    };

    return (
        <Box
            sx={{
                flex: '0 0 300px',
                position: 'sticky',
                insetBlockStart: 8,
                marginBlockStart: 8,
                padding: 4,
                border: '1px solid',
                borderColor: 'border.weak',
                borderRadius: 'md',
                backgroundColor: 'background.weaker',
                fontFamily: 'mono',
            }}
        >
            <Heading level={3} sx={{ marginBlockEnd: 2 }}>
                Proctor overrides
            </Heading>
            <Text variant="secondary" sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}>
                {description ?? (
                    <>
                        Forced buckets are appended to the generated links as{' '}
                        <code>prforceGroups</code>. Set a proctor to <code>-1</code> to leave it
                        unforced.
                    </>
                )}
            </Text>

            <Stack spacing={4} vertical>
                {options.map((proctor) => (
                    <Box
                        key={proctor.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minInlineSize: 0 }}>
                            <label
                                htmlFor={`proctor-${proctor.name}`}
                                style={{
                                    display: 'block',
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </label>
                            <Text
                                variant="secondary"
                                sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 1 }}
                            >
                                {proctor.buckets}
                            </Text>
                        </Box>
                        <input
                            id={`proctor-${proctor.name}`}
                            type="number"
                            min={proctor.min}
                            max={proctor.max}
                            step={1}
                            value={proctorValues[proctor.name]}
                            onChange={(e) => setProctorValue(proctor.name, e.target.value)}
                            onBlur={(e) => {
                                const nextValue = parseBucketValue(e.target.value);
                                setProctorValue(proctor.name, e.target.value);
                                commitWith({ ...proctorValues, [proctor.name]: nextValue });
                            }}
                            style={{
                                width: '64px',
                                flexShrink: 0,
                                fontFamily: 'monospace',
                                fontSize: '13px',
                            }}
                        />
                    </Box>
                ))}

                {customProctors.map((proctor) => (
                    <Box
                        key={proctor.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minInlineSize: 0 }}>
                            <Text
                                sx={{
                                    display: 'block',
                                    fontFamily: 'mono',
                                    fontSize: 'sm',
                                    fontWeight: '700',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </Text>
                            <Text
                                variant="secondary"
                                sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 1 }}
                            >
                                custom · -1 unforced
                            </Text>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                            <select
                                aria-label={`${proctor.name} value`}
                                value={proctor.value}
                                onChange={(e) => {
                                    const nextValue = parseInt(e.target.value, 10);
                                    setCustomProctor(proctor.name, nextValue);
                                    commitWith(
                                        proctorValues,
                                        customProctorsWithValue(proctor.name, nextValue),
                                    );
                                }}
                                onBlur={(e) => {
                                    const nextValue = parseInt(e.target.value, 10);
                                    commitWith(
                                        proctorValues,
                                        customProctorsWithValue(proctor.name, nextValue),
                                    );
                                }}
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                            >
                                {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                aria-label={`Remove ${proctor.name}`}
                                onClick={() => {
                                    removeCustomProctor(proctor.name);
                                    commitWith(
                                        proctorValues,
                                        customProctors.filter(
                                            (entry) => entry.name !== proctor.name,
                                        ),
                                    );
                                }}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </Box>
                    </Box>
                ))}
            </Stack>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text
                    sx={{ display: 'block', fontSize: 'sm', fontWeight: '700', marginBlockEnd: 2 }}
                >
                    Add proctor
                </Text>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                        aria-label="New proctor name"
                        type="text"
                        value={newName}
                        placeholder="proctor_name"
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAdd();
                        }}
                        style={{
                            flex: '1 1 auto',
                            minInlineSize: 0,
                            fontFamily: 'monospace',
                            fontSize: '13px',
                        }}
                    />
                    <select
                        aria-label="New proctor value"
                        value={newValue}
                        onChange={(e) => setNewValue(parseInt(e.target.value, 10))}
                        style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: '13px' }}
                    >
                        {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={!newName.trim()}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text sx={{ display: 'block', fontSize: 'sm', fontWeight: '700' }}>
                    prforceGroups
                </Text>
                <Text
                    variant="secondary"
                    data-testid="proctor-prforce-preview"
                    sx={{ display: 'block', fontSize: 'sm', wordBreak: 'break-all' }}
                >
                    {prforceGroups || '(none forced)'}
                </Text>
            </Box>
        </Box>
    );
};
